import type { LucideIcon } from "lucide-react";
import {
  BarChart2,
  Blocks,
  Bot,
  Building2,
  Check,
  ChevronDown,
  Cloud,
  Code,
  Container,
  Database,
  Download,
  FileCode,
  FileText,
  Folder,
  GitBranch,
  Globe,
  Handshake,
  HardDrive,
  Image,
  Key,
  MessageCircle,
  Presentation,
  Puzzle,
  Search,
  Server,
  Settings,
  Table,
  Terminal,
  User,
  Users,
  Wrench,
  Zap,
} from "lucide-react";
import { useState } from "react";

// ---------------------------------------------------------------------------
// Icon options
// ---------------------------------------------------------------------------

export const ICON_OPTIONS: { name: string; icon: LucideIcon }[] = [
  { name: "puzzle", icon: Puzzle },
  { name: "zap", icon: Zap },
  { name: "globe", icon: Globe },
  { name: "search", icon: Search },
  { name: "code", icon: Code },
  { name: "file-text", icon: FileText },
  { name: "file-code", icon: FileCode },
  { name: "image", icon: Image },
  { name: "building-2", icon: Building2 },
  { name: "cloud", icon: Cloud },
  { name: "database", icon: Database },
  { name: "server", icon: Server },
  { name: "terminal", icon: Terminal },
  { name: "container", icon: Container },
  { name: "git-branch", icon: GitBranch },
  { name: "presentation", icon: Presentation },
  { name: "table", icon: Table },
  { name: "bar-chart-2", icon: BarChart2 },
  { name: "download", icon: Download },
  { name: "wrench", icon: Wrench },
  { name: "key", icon: Key },
  { name: "bot", icon: Bot },
  { name: "users", icon: Users },
  { name: "user", icon: User },
  { name: "handshake", icon: Handshake },
  { name: "message-circle", icon: MessageCircle },
  { name: "folder", icon: Folder },
  { name: "hard-drive", icon: HardDrive },
  { name: "settings", icon: Settings },
  { name: "blocks", icon: Blocks },
];

// ---------------------------------------------------------------------------
// Color presets
// ---------------------------------------------------------------------------

const COLOR_PRESETS = [
  "#FF5500",
  "#EC622B",
  "#FF7A59",
  "#E4002B",
  "#EC4899",
  "#8B5CF6",
  "#7C16F5",
  "#6366F1",
  "#4285F4",
  "#2563EB",
  "#0078D4",
  "#038387",
  "#10B981",
  "#217346",
  "#F59E0B",
  "#C43E1C",
  "#64748B",
  "#2B579A",
];

// ---------------------------------------------------------------------------
// resolveExtensionIcon
// ---------------------------------------------------------------------------

export function resolveExtensionIcon(name: string | null): LucideIcon {
  if (!name) return Blocks;
  const found = ICON_OPTIONS.find((o) => o.name === name);
  return found?.icon ?? Blocks;
}

// ---------------------------------------------------------------------------
// IconPicker
// ---------------------------------------------------------------------------

interface IconPickerProps {
  value: string;
  onChange: (icon: string) => void;
  label?: string;
}

export function IconPicker({ value, onChange, label }: Readonly<IconPickerProps>) {
  const [open, setOpen] = useState(false);
  const CurrentIcon = resolveExtensionIcon(value);

  return (
    <div className="relative">
      {label && <label className="block text-sm font-medium text-content-secondary mb-1.5">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary hover:border-border-hover transition-colors focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
      >
        <CurrentIcon className="w-5 h-5 text-content-secondary" />
        <span className="text-content-primary">{value || "Select icon"}</span>
        <ChevronDown
          className={`w-4 h-4 ml-auto text-content-tertiary transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 bg-surface-primary border border-border-primary rounded-lg shadow-lg p-3 w-full">
          <div className="grid grid-cols-6 gap-1">
            {ICON_OPTIONS.map((opt) => (
              <button
                key={opt.name}
                type="button"
                onClick={() => {
                  onChange(opt.name);
                  setOpen(false);
                }}
                className={`p-2 rounded-lg hover:bg-interactive-hover transition-colors ${
                  value === opt.name ? "bg-brand-primary/20 ring-2 ring-brand-primary" : ""
                }`}
                title={opt.name}
              >
                <opt.icon className="w-5 h-5 text-content-secondary" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ColorPicker
// ---------------------------------------------------------------------------

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
}

export function ColorPicker({ value, onChange, label }: Readonly<ColorPickerProps>) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      {label && <label className="block text-sm font-medium text-content-secondary mb-1.5">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary hover:border-border-hover transition-colors focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
      >
        <div className="w-5 h-5 rounded-full border border-border-secondary" style={{ backgroundColor: value }} />
        <span className="text-content-primary">{value || "Select color"}</span>
        <ChevronDown
          className={`w-4 h-4 ml-auto text-content-tertiary transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 bg-surface-primary border border-border-primary rounded-lg shadow-lg p-3 w-full">
          <div className="grid grid-cols-6 gap-2">
            {COLOR_PRESETS.map((hex) => (
              <button
                key={hex}
                type="button"
                onClick={() => {
                  onChange(hex);
                  setOpen(false);
                }}
                className="w-8 h-8 rounded-full border-2 border-transparent hover:border-content-tertiary flex items-center justify-center transition-colors"
                style={{ backgroundColor: hex }}
                title={hex}
              >
                {value === hex && <Check className="w-4 h-4 text-white" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
